import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Japanese mirror of the corporate documents guide. */
const TITLE = "タイの会社書類を海外で使う — 受理される認証の進め方";
const DESC =
  "タイの法人書類はどの層の認証が必要か、手続きの正しい順序、そして差し戻しを防ぐ提出前チェックを整理します。";
const PATH = "/blog/company-documents-for-abroad";
const URL = `/ja${PATH}`;
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "会社登記証明はどこで取得しますか？",
    answer:
      "タイ商務省事業開発局（DBD）で発行される認証済み謄本を取得します。提出先は発行から一定期間内のものを求めるのが一般的です。",
  },
  {
    question: "บอจ.5（株主名簿）も必要ですか？",
    answer:
      "株主構成の確認が必要な手続きでは求められます。銀行口座開設、投資審査、入札などで提出を要求されることが多い書類です。",
  },
  {
    question: "財務諸表はどの層の認証が必要ですか？",
    answer:
      "取締役の署名、必要に応じて弁護士の立会い、その後にタイ外務省領事局の認証という流れが一般的です。提出先が大使館認証まで求める場合もあります。",
  },
  {
    question: "取締役会決議はどう扱いますか？",
    answer:
      "決議書と授権書は、署名の真正を示すために弁護士（公証人）の立会いを受け、続いて領事局・大使館の認証へ進みます。授権範囲は実際の手続きと一致させてください。",
  },
  {
    question: "翻訳は認証の前後どちらですか？",
    answer:
      "原則として認証後に翻訳します。先に翻訳すると、後で付された認証印や注記が訳文に含まれず差し戻しの原因になります。",
  },
  {
    question: "差し戻しの主な原因は何ですか？",
    answer:
      "書類間で社名や氏名の表記が一致していない、授権チェーンが欠けている、発行が古い、印章・注記の訳し漏れ、添付書類の不足です。",
  },
  {
    question: "手続きにはどのくらいかかりますか？",
    answer:
      "書類の数と認証層により変わります。謄本取得、立会い、領事局認証、大使館認証はそれぞれ営業日を要するため、提出期限から逆算して計画してください。",
  },
];

export const Route = createFileRoute("/ja/blog/company-documents-for-abroad")({
  component: JaCorporateGuide,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "ja" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("company-documents-for-abroad") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("company-documents-for-abroad", "ja") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("company-documents-for-abroad") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "タイ 会社書類 認証, タイ 会社登記証明, บอจ.5 翻訳, タイ 法人書類 領事認証, 取締役会決議 認証",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "ja"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "ホーム", url: "/ja" },
              { name: "ガイド", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("company-documents-for-abroad"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Corporate",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const DOCS: Array<{ doc: string; layer: string }> = [
  { doc: "会社登記証明（直近発行）", layer: "認証謄本 → 領事局 → 大使館" },
  { doc: "株主名簿 บอจ.5", layer: "認証謄本 → 領事局 → 大使館" },
  { doc: "定款・設立関係書類", layer: "認証謄本 → 領事局（要件に応じて）" },
  { doc: "監査済み財務諸表", layer: "取締役署名 → 弁護士立会い → 領事局" },
  { doc: "取締役会決議・授権書", layer: "弁護士立会い → 領事局 → 大使館" },
  { doc: "署名者の身分証明と役職証明", layer: "主要書類に添付して提出" },
];

const STEPS: string[] = [
  "提出先の書面リストで必要書類と有効期間の要件を確認する",
  "事業開発局で直近の認証謄本を取得する",
  "授権チェーン（決議・授権書・署名者の身分証）を整える",
  "署名の真正確認が必要な書類は公証人の立会いを受ける",
  "タイ外務省領事局の認証を受ける",
  "提出先国の在タイ大使館・領事館で認証し、提出前に項目照合する",
];

const CHECKS: string[] = [
  "すべての書類で社名の表記が完全に一致している",
  "各書類の発行日が提出先の有効期間要件を満たしている",
  "授権範囲が実際に行う手続きと一対一で対応している",
  "訳文に印章・エンボス・注記の文言が含まれている",
  "ページ数、添付書類、契印がそろっている",
];

function JaCorporateGuide() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/ja" className="text-muted-foreground hover:text-foreground">
          ホーム
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          ガイド
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="company-documents-for-abroad" locale="ja" />
      <p className="answer-summary speakable-summary">
        タイの会社書類を海外で使う場合、まず事業開発局の直近の認証謄本を取得し、署名の真正確認が必要な書類は公証人の立会いを受けます。
        その後、タイ外務省領事局の認証、提出先国の在タイ大使館・領事館の認証へ進みます。書類間の表記不一致と授権チェーンの欠落が、差し戻しの最大の原因です。
      </p>

      <h2>1. 書類別に必要な認証の層</h2>
      <ul>
        {DOCS.map((d) => (
          <li key={d.doc}>
            <strong>{d.doc}：</strong> {d.layer}
          </li>
        ))}
      </ul>

      <h2>2. 手続きの順序</h2>
      <ol>
        {STEPS.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>3. 提出前チェック</h2>
      <ul>
        {CHECKS.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>

      <h2>4. よくある質問</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>5. 参考リンクと関連サービス</h2>
      <p className="text-sm">
        参考：{" "}
        <a href="https://www.dbd.go.th/" target="_blank" rel="noreferrer">
          タイ商務省 事業開発局（DBD）
        </a>{" "}
        ·{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          タイ外務省 領事局
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/ja/corporate" className="text-primary underline">
            会社・ビジネス法務文書サービス
          </Link>
        </li>
        <li>
          <Link to="/ja/lgz" className="text-primary underline">
            文書認証・領事認証サービス
          </Link>
        </li>
        <li>
          <Link to="/ja/notary" className="text-primary underline">
            公証人（Notary Public）サービス
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        内容確認日 {PUBLISHED}。本ガイドは計画のための一般情報であり、個別事案の法的助言ではありません。手続き前に提出先機関で最新要件をご確認ください。
      </p>
    </article>
  );
}
